import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { getMyBugs, getBugComments, addBugComment } from '../utils/api';
import { FaBug, FaHourglassHalf, FaCheckCircle, FaComment, FaPaperclip, FaEye, FaEyeSlash, FaSpinner, FaSearch, FaUser, FaCalendar, FaEdit, FaTrash } from 'react-icons/fa';
import ImageViewer from '../components/ImageViewer';
import './Bugs.css';

const STATUS_COLORS = {
  'New': '#ff6b6b',
  'Assigned': '#ffb74d',
  'In Progress (Accepted)': '#4ecdc4',
  'Fixed': '#81c784',
  'Fixed (Verified)': '#4caf50',
  'Won\'t fix (Infeasible)': '#9e9e9e',
  'Won\'t fix (Not reproducible)': '#9e9e9e',
  'Won\'t fix (Obsolete)': '#9e9e9e',
  'Won\'t fix (Intended behavior)': '#9e9e9e',
  'Inactive': '#9e9e9e',
  'Duplicate': '#9e9e9e'
};

const MODULE_ICONS = {
  'Dashboard': <span title="Dashboard" style={{fontSize:'1.1em'}}>📊</span>,
  'Organization Structure': <span title="Organization Structure" style={{fontSize:'1.1em'}}>🏢</span>,
  'Workflows': <span title="Workflows" style={{fontSize:'1.1em'}}>🔄</span>,
  'Company OKRs': <span title="Company OKRs" style={{fontSize:'1.1em'}}>🎯</span>,
  'Personal OKRs': <span title="Personal OKRs" style={{fontSize:'1.1em'}}>👤</span>,
  'Team OKRs': <span title="Team OKRs" style={{fontSize:'1.1em'}}>👥</span>,
  'My OKRs': <span title="My OKRs" style={{fontSize:'1.1em'}}>📋</span>,
  'Annual OKRs': <span title="Annual OKRs" style={{fontSize:'1.1em'}}>📅</span>,
  'People': <span title="People" style={{fontSize:'1.1em'}}>👥</span>,
  'Teams': <span title="Teams" style={{fontSize:'1.1em'}}>👥</span>,
  'Departments': <span title="Departments" style={{fontSize:'1.1em'}}>🏢</span>,
  'Visual Map': <span title="Visual Map" style={{fontSize:'1.1em'}}>🗺️</span>,
  'Safety Procedures': <span title="Safety Procedures" style={{fontSize:'1.1em'}}>🛡️</span>,
  'SLAs': <span title="SLAs" style={{fontSize:'1.1em'}}>⏱️</span>,
  'Chat': <span title="Chat" style={{fontSize:'1.1em'}}>💬</span>,
  'Cost Cards': <span title="Cost Cards" style={{fontSize:'1.1em'}}>💸</span>,
  'Template Picker': <span title="Template Picker" style={{fontSize:'1.1em'}}>📄</span>,
  'Create Cost Card': <span title="Create Cost Card" style={{fontSize:'1.1em'}}>➕</span>,
  'Edit Cost Card': <span title="Edit Cost Card" style={{fontSize:'1.1em'}}>✏️</span>,
  'Create Custom Template': <span title="Create Custom Template" style={{fontSize:'1.1em'}}>📝</span>,
  'Edit Custom Template': <span title="Edit Custom Template" style={{fontSize:'1.1em'}}>✏️</span>,
  'Create OKR': <span title="Create OKR" style={{fontSize:'1.1em'}}>➕</span>,
  'Edit OKR': <span title="Edit OKR" style={{fontSize:'1.1em'}}>✏️</span>,
  'Technical Sales Workflow': <span title="Technical Sales Workflow" style={{fontSize:'1.1em'}}>🔧</span>,
  'Store Management Workflow': <span title="Store Management Workflow" style={{fontSize:'1.1em'}}>🏪</span>,
  'Sales Workflow': <span title="Sales Workflow" style={{fontSize:'1.1em'}}>💰</span>,
  'Import Workflow': <span title="Import Workflow" style={{fontSize:'1.1em'}}>📦</span>,
  'Job Descriptions': <span title="Job Descriptions" style={{fontSize:'1.1em'}}>📋</span>,
  'Employees Reviews': <span title="Employees Reviews" style={{fontSize:'1.1em'}}>📝</span>,
  'Interview Processes': <span title="Interview Processes" style={{fontSize:'1.1em'}}>🤝</span>,
  'Company Strategy': <span title="Company Strategy" style={{fontSize:'1.1em'}}>🎯</span>,
  'Business Continuity Plan': <span title="Business Continuity Plan" style={{fontSize:'1.1em'}}>📋</span>,
  'Data Management SOP': <span title="Data Management SOP" style={{fontSize:'1.1em'}}>📊</span>,
  'Procurement SOP': <span title="Procurement SOP" style={{fontSize:'1.1em'}}>🛒</span>,
  'Risk Compliance SOP': <span title="Risk Compliance SOP" style={{fontSize:'1.1em'}}>⚠️</span>,
  'Settings': <span title="Settings" style={{fontSize:'1.1em'}}>⚙️</span>,
  'Search Results': <span title="Search Results" style={{fontSize:'1.1em'}}>🔍</span>,
  'Other': <span title="Other" style={{fontSize:'1.1em'}}>❓</span>,
  default: <FaBug style={{fontSize:'1.1em'}} />
};

function StatusBadge({ status }) {
  return (
    <span
      className="bug-status-badge"
      style={{ background: STATUS_COLORS[status] || '#bdbdbd' }}
    >
      {status === 'New' && <FaBug style={{ marginRight: 4 }} />}
      {status === 'Assigned' && <FaHourglassHalf style={{ marginRight: 4 }} />}
      {status === 'In Progress (Accepted)' && <FaHourglassHalf style={{ marginRight: 4 }} />}
      {status === 'Fixed' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status === 'Fixed (Verified)' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status.startsWith('Won\'t fix') && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status === 'Inactive' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status === 'Duplicate' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status}
    </span>
  );
}

function PriorityBadge({ priority }) {
  return <span className={`bug-priority ${priority.toLowerCase()}`}>{priority}</span>;
}

function Toast({ message, onClose }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 2500);
    return () => clearTimeout(t);
  }, [message, onClose]);
  if (!message) return null;
  return <div className="toast">{message}</div>;
}

// Comment component for displaying individual comments
function Comment({ comment, onEdit, onDelete, onReply, currentUser, isEditing, onSave, onCancel, level = 0, bugId }) {
  const [editContent, setEditContent] = useState(comment.content);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');

  const isAuthor = comment.authorEmail === currentUser?.email;
  const isAdmin = currentUser?.role === 'super';

  const handleSave = () => {
    onSave(comment._id, editContent);
  };

  const handleCancel = () => {
    setEditContent(comment.content);
    onCancel();
  };

  const handleReply = () => {
    if (replyContent.trim()) {
      onReply(bugId, comment._id, replyContent);
      setReplyContent('');
      setShowReplyForm(false);
    }
  };

  if (isEditing) {
    return (
      <div className="bug-comment editing">
        <div className="comment-header">
          <div className="comment-author">
            <FaUser /> {comment.author}
          </div>
          <div className="comment-time">
            <FaCalendar /> {new Date(comment.timestamp).toLocaleString()}
          </div>
        </div>
        <textarea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          className="comment-edit-textarea"
          rows={3}
        />
        <div className="comment-actions">
          <button onClick={handleSave} className="btn-save">Save</button>
          <button onClick={handleCancel} className="btn-cancel">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bug-comment ${comment.isInternal ? 'internal' : ''}`} style={{ marginLeft: `${level * 20}px` }}>
      {/* Show parent comment if this is a reply */}
      {comment.parentCommentId && (
        <div className="parent-comment">
          <div className="parent-comment-header">
            <span className="parent-label">Replying to:</span>
            <span className="parent-author">{comment.parentCommentId.author}</span>
            <span className="parent-time">{new Date(comment.parentCommentId.timestamp).toLocaleString()}</span>
          </div>
          <div className="parent-comment-content">
            {comment.parentCommentId.content.split('\n').map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        </div>
      )}
      
      <div className="comment-header">
        <div className="comment-author">
          <FaUser /> {comment.author}
          {comment.isInternal && <span className="internal-badge">IT Team</span>}
        </div>
        <div className="comment-time">
          <FaCalendar /> {new Date(comment.timestamp).toLocaleString()}
        </div>
        <div className="comment-actions">
          <button onClick={() => setShowReplyForm(!showReplyForm)} className="btn-reply">
            Reply
          </button>
          {(isAuthor || isAdmin) && (
            <>
              <button onClick={() => onEdit(comment._id)} className="btn-edit">
                <FaEdit />
              </button>
              <button onClick={() => onDelete(comment._id)} className="btn-delete">
                <FaTrash />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="comment-content">
        {comment.content.split('\n').map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>
      
      {/* Reply Form */}
      {showReplyForm && (
        <div className="reply-form">
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Write a reply..."
            rows={2}
            className="reply-textarea"
          />
          <div className="reply-actions">
            <button onClick={handleReply} disabled={!replyContent.trim()} className="btn-submit-reply">
              Reply
            </button>
            <button onClick={() => setShowReplyForm(false)} className="btn-cancel-reply">
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="comment-replies">
          {console.log('🔍 Rendering replies for comment', comment._id, ':', comment.replies)}
          {comment.replies.map(reply => (
            <Comment
              key={reply._id}
              comment={reply}
              currentUser={currentUser}
              isEditing={isEditing}
              onEdit={onEdit}
              onDelete={onDelete}
              onReply={onReply}
              onSave={onSave}
              onCancel={onCancel}
              level={level + 1}
              bugId={bugId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function MySupportTickets() {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [toast, setToast] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [commentForm, setCommentForm] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [comments, setComments] = useState({}); // Store comments for each bug
  const [editingComment, setEditingComment] = useState(null);
  const { token, user } = useContext(AuthContext);
  
  // Debug: Log user info
  useEffect(() => {
    console.log('🔍 MySupportTickets - Current user:', user);
    console.log('🔍 MySupportTickets - Token exists:', !!token);
  }, [user, token]);
  const searchRef = useRef();
  const debounceTimeout = useRef();

  useEffect(() => {
    // Test debug endpoints first
    fetch('/api/support/debug-bugs')
      .then(res => res.json())
      .then(data => {
        console.log('🔍 Debug: Total bugs in database:', data.totalBugs);
      })
      .catch(err => {
        console.error('❌ Debug endpoint failed:', err);
      });
    
    fetch('/api/support/debug-emails')
      .then(res => res.json())
      .then(data => {
        console.log('📧 Debug: All emails in database:', data.emails);
      })
      .catch(err => {
        console.error('❌ Debug emails endpoint failed:', err);
      });
    
    fetchMyBugs();
  }, []);

  const fetchMyBugs = async () => {
    try {
      console.log('🔍 Fetching my bugs with token:', token ? 'Token exists' : 'No token');
      
      // Try the API utility first
      try {
        const data = await getMyBugs(token);
        console.log('📊 Received bugs data from API utility:', data);
        setBugs(data);
      } catch (apiError) {
        console.log('⚠️ API utility failed, trying direct fetch:', apiError);
        
        // Fallback to direct fetch like Bugs.js
        const response = await fetch('/api/support/my-bugs', {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('📊 Received bugs data from direct fetch:', data);
        setBugs(data);
      }
    } catch (error) {
      console.error('❌ Error fetching bugs:', error);
      setToast('Failed to load your support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    debounceTimeout.current = setTimeout(() => {
      searchRef.current = value;
    }, 300);
  };

  const filteredBugs = bugs.filter(bug => {
    const matchesSearch = !search || 
      bug.ticketId.toLowerCase().includes(search.toLowerCase()) ||
      bug.issueTitle.toLowerCase().includes(search.toLowerCase()) ||
      bug.description.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = !statusFilter || bug.status === statusFilter;
    const matchesModule = !moduleFilter || bug.module === moduleFilter;
    
    return matchesSearch && matchesStatus && matchesModule;
  });

  // Fetch comments for a specific bug
  const fetchComments = async (bugId) => {
    try {
      const response = await fetch(`/api/support/bugs/${bugId}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (response.ok) {
        const commentsData = await response.json();
        console.log('🔍 Fetched comments for bug', bugId, ':', commentsData);
        setComments(prev => ({ ...prev, [bugId]: commentsData }));
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const toggleExpanded = async (bugId) => {
    const newExpandedState = !expandedRows[bugId];
    setExpandedRows(prev => ({
      ...prev,
      [bugId]: newExpandedState
    }));
    
    // Fetch comments when expanding
    if (newExpandedState && !comments[bugId]) {
      await fetchComments(bugId);
    }
  };

  const handleCommentChange = (bugId, value) => {
    setCommentForm(prev => ({
      ...prev,
      [bugId]: value
    }));
  };

  const handleFileChange = (bugId, files) => {
    setCommentForm(prev => ({
      ...prev,
      [`${bugId}_files`]: Array.from(files)
    }));
  };

  const submitComment = async (bugId) => {
    if (!commentForm[bugId]?.trim()) return;
    
    setSubmittingComment(prev => ({ ...prev, [bugId]: true }));
    
    try {
      const formData = new FormData();
      formData.append('content', commentForm[bugId]);
      formData.append('isInternal', false);
      
      const files = commentForm[`${bugId}_files`] || [];
      files.forEach(file => {
        formData.append('attachments', file);
      });
      
      await addBugComment(bugId, formData, token);
      
      // Clear form
      setCommentForm(prev => {
        const newForm = { ...prev };
        delete newForm[bugId];
        delete newForm[`${bugId}_files`];
        return newForm;
      });
      
      setToast('Comment added successfully');
      
      // Refresh the bug data to show new comment
      fetchMyBugs();
      
      // Refresh comments for this specific bug
      await fetchComments(bugId);
    } catch (error) {
      setToast('Failed to add comment');
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(prev => ({ ...prev, [bugId]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isImage = (filename) => {
    return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filename || '');
  };

  const getImageUrl = (attachment, attachmentData, index) => {
    // If we have attachmentData with processed images, use compressed version
    if (attachmentData && attachmentData[index]) {
      return attachmentData[index].compressedUrl || attachment;
    }
    return attachment;
  };

  const getThumbnailUrl = (attachment, attachmentData, index) => {
    // If we have attachmentData with processed images, use thumbnail version
    if (attachmentData && attachmentData[index]) {
      return attachmentData[index].thumbnailUrl || attachment;
    }
    return attachment;
  };

  const handleImageClick = (attachment, attachmentData, index) => {
    const imageUrl = getImageUrl(attachment, attachmentData, index);
    setSelectedImage({
      src: imageUrl,
      alt: attachment.split('/').pop() || 'Image'
    });
  };

  // Edit a comment
  const handleEditComment = (commentId) => {
    setEditingComment(commentId);
  };

  // Save edited comment
  const handleSaveComment = async (bugId, commentId, content) => {
    try {
      const response = await fetch(`/api/support/bugs/${bugId}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });

      if (response.ok) {
        const updatedComment = await response.json();
        setComments(prev => ({
          ...prev,
          [bugId]: prev[bugId].map(comment => 
            comment._id === commentId ? updatedComment : comment
          )
        }));
        setEditingComment(null);
      }
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  // Delete a comment
  const handleDeleteComment = async (bugId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const response = await fetch(`/api/support/bugs/${bugId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      if (response.ok) {
        setComments(prev => ({
          ...prev,
          [bugId]: prev[bugId].filter(comment => comment._id !== commentId)
        }));
        
        // Refresh the bug data to update comment count
        fetchMyBugs();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  // Cancel comment editing
  const handleCancelEdit = () => {
    setEditingComment(null);
  };

  // Handle reply to a comment
  const handleReplyToComment = async (bugId, parentCommentId, content) => {
    try {
      const response = await fetch(`/api/support/bugs/${bugId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ content, parentCommentId }),
      });

      if (response.ok) {
        const newReply = await response.json();
        // Refresh comments to show the new reply
        await fetchComments(bugId);
        
        // Refresh the bug data to update comment count
        fetchMyBugs();
      }
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  if (loading) {
    return (
      <div className="bugs-admin-page">
        <div className="bugs-header-row">
          <h1>My Support Tickets</h1>
        </div>
        <div className="bugs-loading">
          <FaSpinner className="spin" /> Loading your support tickets...
        </div>
      </div>
    );
  }

  return (
    <div className="bugs-admin-page">
      <div className="bugs-header-row">
        <h1>My Support Tickets</h1>
        <div className="bugs-subtitle">View and manage your submitted support tickets</div>
      </div>

      {/* Filters */}
      <div className="bugs-toolbar">
        <div className="bugs-search">
          <FaSearch style={{ color: '#666' }} />
          <input
            type="text"
            placeholder="Search tickets..."
            value={search}
            onChange={handleSearch}
          />
        </div>
        
        <select className="bugs-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="New">New</option>
          <option value="Assigned">Assigned</option>
          <option value="In Progress (Accepted)">In Progress (Accepted)</option>
          <option value="Fixed">Fixed</option>
          <option value="Fixed (Verified)">Fixed (Verified)</option>
          <option value="Won't fix (Infeasible)">Won't fix (Infeasible)</option>
          <option value="Won't fix (Not reproducible)">Won't fix (Not reproducible)</option>
          <option value="Won't fix (Obsolete)">Won't fix (Obsolete)</option>
          <option value="Won't fix (Intended behavior)">Won't fix (Intended behavior)</option>
          <option value="Inactive">Inactive</option>
          <option value="Duplicate">Duplicate</option>
        </select>
        
        <select className="bugs-module-filter" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
          <option value="">All Modules</option>
          {Array.from(new Set(bugs.map(bug => bug.module))).map(module => (
            <option key={module} value={module}>{module}</option>
          ))}
        </select>
      </div>

      {/* Results count */}
      <div style={{ marginBottom: '16px', color: '#666', fontSize: '0.9rem' }}>
        <span>{filteredBugs.length} ticket{filteredBugs.length !== 1 ? 's' : ''} found</span>
      </div>

      {/* Bugs table */}
      <div className="bugs-table-wrapper">
        {filteredBugs.length === 0 ? (
          <div className="bugs-empty">
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {bugs.length === 0 ? (
                <div>
                  <FaBug style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                  <h3>No support tickets yet</h3>
                  <p>When you submit a support ticket, it will appear here.</p>
                </div>
              ) : (
                <div>
                  <h3>No tickets match your search</h3>
                  <p>Try adjusting your search terms or filters.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <table className="bugs-table">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Issue</th>
                <th>Module</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Date</th>
                <th>Comments</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBugs.map(bug => (
                <React.Fragment key={bug._id}>
                  <tr className={expandedRows[bug._id] ? 'expanded' : ''}>
                    <td>
                      <span className="bug-ticket">{bug.ticketId}</span>
                    </td>
                    <td>
                      <div className="bug-title">{bug.issueTitle}</div>
                    </td>
                    <td>
                      <div className="bug-module">
                        {MODULE_ICONS[bug.module] || MODULE_ICONS.default}
                        {bug.module}
                      </div>
                    </td>
                    <td>
                      <StatusBadge status={bug.status} />
                    </td>
                    <td>
                      <PriorityBadge priority={bug.priority} />
                    </td>
                    <td>
                      <div className="bug-date">{formatDate(bug.timestamp)}</div>
                    </td>
                    <td>
                      <div className="bug-comments">
                        <FaComment style={{ marginRight: 4 }} />
                        {bug.commentCount || 0}
                      </div>
                    </td>
                    <td>
                      <button
                        className="expand-btn"
                        onClick={() => toggleExpanded(bug._id)}
                        title={expandedRows[bug._id] ? 'Hide details' : 'Show details'}
                      >
                        {expandedRows[bug._id] ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </td>
                  </tr>
                  
                  {expandedRows[bug._id] && (
                    <tr className="expanded-details">
                      <td colSpan="8">
                        <div className="bug-details">
                          <div className="bug-detail-section">
                            <h4>Description</h4>
                            <p>{bug.description}</p>
                            {bug.errorMessage && (
                              <div className="error-message">
                                <strong>Error Message:</strong> {bug.errorMessage}
                              </div>
                            )}
                          </div>
                          
                          <div className="bug-detail-section">
                            <h4>Expected vs Actual</h4>
                            <p>{bug.expected}</p>
                          </div>
                          
                          {bug.steps && bug.steps.length > 0 && (
                            <div className="bug-detail-section">
                              <h4>Steps to Reproduce</h4>
                              <ol>
                                {bug.steps.map((step, index) => (
                                  <li key={index}>{step}</li>
                                ))}
                              </ol>
                            </div>
                          )}
                          
                          {bug.attachment && (
                            <div className="bug-detail-section">
                              <h4>Original Attachment</h4>
                              {isImage(bug.attachment) ? (
                                <div className="image-attachment">
                                  <img 
                                    src={bug.attachmentData?.thumbnailUrl || bug.attachment} 
                                    alt="Original attachment" 
                                    className="attachment-preview"
                                    onClick={() => setSelectedImage({
                                      src: bug.attachmentData?.compressedUrl || bug.attachment,
                                      alt: bug.attachment.split('/').pop() || 'Original attachment'
                                    })}
                                    onError={(e) => {
                                      console.log('❌ Image failed to load:', e.target.src);
                                      e.target.style.display = 'none';
                                      // Add a fallback message
                                      const fallbackDiv = document.createElement('div');
                                      fallbackDiv.innerHTML = '<p style="padding: 10px; text-align: center; color: #666; font-size: 12px;">Image not available</p>';
                                      e.target.parentNode.appendChild(fallbackDiv);
                                    }}
                                  />
                                  <a 
                                    href={bug.attachmentData?.compressedUrl || bug.attachment} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="attachment-link"
                                  >
                                    <FaPaperclip /> View Attachment
                                  </a>
                                </div>
                              ) : (
                                <a 
                                  href={bug.attachment} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="attachment-link"
                                >
                                  <FaPaperclip /> View Attachment
                                </a>
                              )}
                            </div>
                          )}
                          
                          {/* Comments Section */}
                          <div className="bug-detail-section">
                            <h4>Comments ({comments[bug._id]?.length || 0})</h4>
                            
                            {/* Comments List */}
                            <div className="comments-list">
                              {comments[bug._id]?.map(comment => (
                                <Comment
                                  key={comment._id}
                                  comment={comment}
                                  currentUser={user}
                                  isEditing={editingComment === comment._id}
                                  onEdit={handleEditComment}
                                  onDelete={(commentId) => handleDeleteComment(bug._id, commentId)}
                                  onReply={(bugId, parentCommentId, content) => handleReplyToComment(bugId, parentCommentId, content)}
                                  onSave={(commentId, content) => handleSaveComment(bug._id, commentId, content)}
                                  onCancel={handleCancelEdit}
                                  bugId={bug._id}
                                />
                              ))}
                              {(!comments[bug._id] || comments[bug._id].length === 0) && (
                                <div className="no-comments">No comments yet. Be the first to add one!</div>
                              )}
                            </div>
                            
                            {/* Comment Form for open tickets */}
                            {bug.status !== 'Resolved' && (
                              <div className="comment-form">
                                <textarea
                                  value={commentForm[bug._id] || ''}
                                  onChange={(e) => handleCommentChange(bug._id, e.target.value)}
                                  placeholder="Add additional information or ask a question..."
                                  rows={3}
                                  style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #ddd', marginBottom: '10px' }}
                                />
                                
                                <div className="comment-form-actions">
                                  <input
                                    type="file"
                                    multiple
                                    accept="image/*,.log,.txt,.pdf"
                                    onChange={(e) => handleFileChange(bug._id, e.target.files)}
                                    style={{ marginRight: '10px' }}
                                  />
                                  <button
                                    onClick={() => submitComment(bug._id)}
                                    disabled={!commentForm[bug._id]?.trim() || submittingComment[bug._id]}
                                    style={{
                                      padding: '8px 16px',
                                      background: '#004080',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: 6,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {submittingComment[bug._id] ? 'Adding...' : 'Add Comment'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Toast message={toast} onClose={() => setToast('')} />

      {/* Image Viewer Modal */}
      {selectedImage && (
        <ImageViewer
          src={selectedImage.src}
          alt={selectedImage.alt}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
} 